import type { Direction } from "@2048/game-core";

interface InputEventMap {
  move: Direction;
  restart: void;
  keepPlaying: void;
}

type Listener<T> = (data: T) => void;

/** Maps keyboard keys (arrows, WASD, Vim) to a direction. */
const KEY_TO_DIRECTION: Record<string, Direction> = {
  ArrowUp: 0,
  ArrowRight: 1,
  ArrowDown: 2,
  ArrowLeft: 3,
  k: 0,
  l: 1,
  j: 2,
  h: 3,
  w: 0,
  d: 1,
  s: 2,
  a: 3,
};

const SWIPE_THRESHOLD = 10;

/**
 * Emits "move" / "restart" / "keepPlaying" from keyboard, on-screen buttons and
 * touch swipes. Ported from the original; the IE10 `MSPointer` path is dropped.
 */
export class KeyboardInputManager {
  private readonly events = new Map<keyof InputEventMap, Listener<never>[]>();

  constructor() {
    this.listen();
  }

  on<K extends keyof InputEventMap>(
    event: K,
    callback: Listener<InputEventMap[K]>,
  ): void {
    const listeners = this.events.get(event) ?? [];
    listeners.push(callback as Listener<never>);
    this.events.set(event, listeners);
  }

  private emit<K extends keyof InputEventMap>(
    event: K,
    data: InputEventMap[K],
  ): void {
    const listeners = this.events.get(event);
    if (!listeners) return;
    for (const callback of listeners) {
      (callback as Listener<InputEventMap[K]>)(data);
    }
  }

  private listen(): void {
    document.addEventListener("keydown", (event) => {
      const modifiers =
        event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
      if (modifiers || this.targetIsInput(event)) return;

      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
      const direction = KEY_TO_DIRECTION[key];
      if (direction !== undefined) {
        event.preventDefault();
        this.emit("move", direction);
      } else if (key === "r") {
        event.preventDefault();
        this.emit("restart", undefined);
      }
    });

    this.bindButtonPress(".retry-button", () => this.emit("restart", undefined));
    this.bindButtonPress(".restart-button", () =>
      this.emit("restart", undefined),
    );
    this.bindButtonPress(".keep-playing-button", () =>
      this.emit("keepPlaying", undefined),
    );

    this.listenForSwipes();
  }

  private listenForSwipes(): void {
    const gameContainer = document.querySelector<HTMLElement>(".game-container");
    if (!gameContainer) return;

    let startX = 0;
    let startY = 0;

    gameContainer.addEventListener(
      "touchstart",
      (event) => {
        if (
          event.touches.length > 1 ||
          event.targetTouches.length > 1 ||
          this.targetIsInput(event) ||
          this.targetIsLink(event)
        ) {
          return;
        }
        const touch = event.touches[0];
        if (!touch) return;
        startX = touch.clientX;
        startY = touch.clientY;
        event.preventDefault();
      },
      { passive: false },
    );

    gameContainer.addEventListener(
      "touchmove",
      (event) => {
        event.preventDefault();
      },
      { passive: false },
    );

    gameContainer.addEventListener("touchend", (event) => {
      if (
        event.touches.length > 0 ||
        event.targetTouches.length > 0 ||
        this.targetIsInput(event) ||
        this.targetIsLink(event)
      ) {
        return;
      }
      const touch = event.changedTouches[0];
      if (!touch) return;

      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      if (Math.max(absDx, absDy) > SWIPE_THRESHOLD) {
        // (right : left) : (down : up)
        const direction: Direction =
          absDx > absDy ? (dx > 0 ? 1 : 3) : dy > 0 ? 2 : 0;
        this.emit("move", direction);
      }
    });
  }

  private bindButtonPress(selector: string, handler: () => void): void {
    const button = document.querySelector(selector);
    if (!button) return;
    const onPress = (event: Event) => {
      event.preventDefault();
      handler();
    };
    button.addEventListener("click", onPress);
    button.addEventListener("touchend", onPress);
  }

  private targetIsInput(event: Event): boolean {
    const target = event.target as HTMLElement | null;
    return target?.tagName?.toLowerCase() === "input";
  }

  private targetIsLink(event: Event): boolean {
    const target = event.target as HTMLElement | null;
    return target?.tagName?.toLowerCase() === "a";
  }
}
